import { useCallback, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { PageRoutes } from "../../../constants/routes";
import { StoreSearchType } from "../../../ducks/store";
import { CouponHook } from "../../../hooks/useCoupon";
import { useMenu } from "../../../hooks/useMenu";
import { useNavigation } from "../../../hooks/useNavigation";
import {
  convertToISO8601,
  formatDateTime,
  getDatePlusMinutes,
  isDateTimeBefore,
} from "../../../utils/date";

export const useMenuList = (useCoupon: CouponHook) => {
  const { navigateToTopPage, navigateToPage } = useNavigation();
  const { storeCode, date } = useParams<{ storeCode: string; date: string }>();

  const {
    store,
    menuMapById,
    recommendedMenus,
    limitedMenus,
    searchMenus,
    categoriesToDisplay,
    handleSetFavoriteStore,
    handleSetFavoriteMenu,
  } = useMenu(useCoupon, storeCode, date);

  const {
    appliedCoupons,
    selectedAppCoupons,
    applyCoupon,
    handleRemoveCoupon,
    getCouponName,
  } = useCoupon;

  useEffect(() => {
    if (!store || !date) return;

    searchMenus(store.id, date);
  }, [store, date, appliedCoupons, selectedAppCoupons]);

  useEffect(() => {
    if (!store || !date) return;

    const shortestPickupDate = getDatePlusMinutes(
      new Date(),
      store.shortestTimeUntilPickup,
    );

    if (isDateTimeBefore(date, shortestPickupDate)) {
      navigateToPage(
        PageRoutes.MENU_LIST.replace(":storeCode", store.storeCode).replace(
          ":date",
          convertToISO8601(shortestPickupDate),
        ),
      );
    }
  }, [store, date]);

  const pickupDateText = useMemo(() => {
    if (!date) return "";

    return formatDateTime(new Date(date), true);
  }, [date]);

  const handleOnClickChangePickupDate = useCallback(() => {
    if (!storeCode) return;

    navigateToPage(
      PageRoutes.SHOP_SEARCH.replace(
        ":type",
        StoreSearchType.StoreCode.valueOf(),
      ).replace(":query", storeCode),
      undefined,
      { selectedDate: date },
    );
  }, [storeCode]);

  return {
    store,
    date,
    recommendedMenus,
    limitedMenus,
    categoriesToDisplay,
    pickupDateText,
    handleSetFavoriteStore,
    navigateToTopPage,
    handleOnClickChangePickupDate,
    handleSetFavoriteMenu,
    menuMapById,
    appliedCoupons,
    applyCoupon,
    handleRemoveCoupon,
    getCouponName,
  };
};
